import "./App.css";
import { useEffect, useState } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";

import { MainDisplay } from "./components/mainDisplay";
import { MenuBar } from "./components/menuBar";
import ProgramMenuTreeView2 from "./components/programMenuTreeView2";
import { SubSideBar2 } from "./components/subSidBar2";
import SearchModal from "./components/searchModal";

import {
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  setPersistence,
  browserSessionPersistence,
} from "firebase/auth";
import { auth } from "./firebase";
import defaultAxios from "axios";

import { useAuth } from "./AuthContext";
import { backendUrl } from "./common/constantVarialble";

import AddProgramPage from "./pages/admin/addProgram/addProgramPage";
import { AdminMainPage } from "./pages/admin/adminMainPage";
import { ProgramDetailPage } from "./pages/admin/programDetail/programDetailPage";
import { CreateServicePostHome } from "./pages/admin/createPost/createServicePostHome";
import { CreateServicePost } from "./pages/admin/createPost/createServicePost";
import { PostingDetailScreen } from "./pages/admin/createPost/postingDetailScreen";
import { PostingUpdateScreen } from "./pages/admin/createPost/postingUpdateScreen";
import { HomeMainPage } from "./pages/home/homeMainPage";
import PrivacyPage from "./pages/home/legal/privacyPage";
import TermsPage from "./pages/home/legal/termsPage";
import { AccountDeletionPage, SupportPage } from "./pages/home/legal/servicePages";


function App() {
  interface ProgramDepthParent {
    code: string;
    p_code: string;
    depth: number;
  }

  const navigate = useNavigate();
  const location = useLocation();
  const isHome = !location.pathname.startsWith("/admin"); // 홈페이지 화면 여부

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [programIDList, setProgramIDList] = useState<any[]>([]);
  const [programDepthParent, setProgramDepthParent] =
    useState<ProgramDepthParent | null>(null);

  // useAuth 훅 사용하여 user 정보 가져오기
  const { user, role } = useAuth();

  const toggleSidebar = () => setSidebarOpen((prevState) => !prevState);
  const closeToggleSideBar = () => setSidebarOpen(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchResult, setSearchResult] = useState(""); // 지금은 사용 안 해도 OK
  const [isSignIn, setIsSignIn] = useState(false);

  const createUser = async (
    p_user_id: string,
    p_user_name: string,
    p_name: string,
    p_email: string
  ) => {
    try {
      const { data } = await defaultAxios({
        method: "post",
        url: backendUrl,
        data: {
          program_id: "PERCENT-ADMIN-USER-C-CREATEUSERBYGMAIL",
          params: {
            p_user_id,
            p_name,
            p_user_name,
            p_email,
          },
          page_index: -1,
        },
      });

      return data;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const isExistingUser = async (p_user_id: string): Promise<boolean> => {
    try {
      const { data } = await defaultAxios({
        method: "post",
        url: backendUrl,
        data: {
          program_id: "PERCENT-ADMIN-USER-L-LSTBYUSERID",
          params: {
            p_user_id,
          },
          page_index: -1,
        },
      });

      return !!(data && data.ok && data.data.length > 0);
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const provider = new GoogleAuthProvider();

  const handleGoogleLogin = async () => {
    try {
      await setPersistence(auth, browserSessionPersistence);
      const userCredential = await signInWithPopup(auth, provider);

      const userExists = await isExistingUser(userCredential.user.uid);

      if (!userExists) {
        const randomUserName = Math.random().toString(36).substring(2, 12);
        await createUser(
          userCredential.user.uid,
          randomUserName,
          userCredential.user.displayName ?? "",
          userCredential.user.email ?? ""
        );
      }

      setIsSignIn(true);
    } catch (error) {
      console.log(error);
    }
  };

  const handleGoogleLogout = () => {
    signOut(auth)
      .then(() => {
        setIsSignIn(false);
        window.location.reload();
      })
      .catch((error) => console.log(error));
  };

  /**
   * ✅ 전역 가드(로그인 체크)는 "관리자 화면"에서만 동작하게
   * - 홈(/)은 누구나 접근 가능
   * - 관리자 경로에서만 권한 없으면 /admin으로 보냄
   */
  useEffect(() => {
    const path = location.pathname;

    const isAdminArea =
      path.startsWith("/admin");

    if (!isAdminArea) return;

    if (!user) {
      navigate("/admin");
      return;
    }
    if (role !== "ADMIN") {
      navigate("/admin");
      return;
    }
  }, [location.pathname, user, role, navigate]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setIsSignIn(!!u);
    });
    return () => unsubscribe();
  }, []);

  const closeModal = () => setIsModalOpen(false);

  const handleSearch = (searchTerm: string) => {
    setSearchResult(`Search result for "${searchTerm}"`);
    closeModal();
  };

  return (
    <div className="flex flex-row flex-1">
      {isModalOpen && (
        <SearchModal onClose={closeModal} onSearch={handleSearch} />
      )}

      {/* ✅ 홈에서는 MenuBar/SubSideBar 숨김 */}
      {!isHome && (
        <>
          <MenuBar
            toggleSidBar={toggleSidebar}
            closeToggleSideBar={closeToggleSideBar}
            setIsModalOpen={setIsModalOpen}
            isSignIn={user || isSignIn ? true : false}
            handleGoogleLogin={handleGoogleLogin}
            handleGoogleLogout={handleGoogleLogout}
          />

          <SubSideBar2 sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}>
            <ProgramMenuTreeView2
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              setProgramIDList={setProgramIDList}
              programIDList={programIDList}
              setProgramDepthParent={setProgramDepthParent}
            />
          </SubSideBar2>
        </>
      )}

      <Routes>
        {/* ✅ HOME PAGE: 홈은 홈페이지만 */}
        <Route path="/" element={<HomeMainPage isSignIn={isSignIn} />} />

        <Route path="/privacy" element={<PrivacyPage />} />

        <Route path="/terms" element={<TermsPage />} />

        <Route path="/support" element={<SupportPage />} />

        <Route path="/account-deletion" element={<AccountDeletionPage />} />

        {/* ✅ ADMIN GATE PAGE (로그인/권한 안내 페이지) */}
        <Route path="/admin" element={<AdminMainPage isSignIn={isSignIn} />} />

        {/* ✅ 관리자 기능들(기존 유지) */}
        <Route
          path="admin/sql"
          element={
            <div className="flex flex-row flex-1">
              <MainDisplay data={programIDList} />
            </div>
          }
        />
        <Route
          path="admin/sql/addprogram"
          element={<AddProgramPage programDepthParent={programDepthParent} />}
        />
        <Route path="admin/sql/program/" element={<ProgramDetailPage />} />
        <Route path="admin/create-service-post-home" element={<CreateServicePostHome />} />
        <Route path="admin/create-service-post" element={<CreateServicePost />} />
        <Route path="admin/posting" element={<PostingDetailScreen />} />
        <Route path="admin/edit-post" element={<PostingUpdateScreen />} />
      </Routes>
    </div>
  );
}

export default App;
